import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAdmin } from '@/hooks/usePlatformAdmin';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OrganizationManagement } from '@/components/platform-admin/OrganizationManagement';
import { UserManagement } from '@/components/platform-admin/UserManagement';
import { PlatformMetrics } from '@/components/platform-admin/PlatformMetrics';
import { AuditLogViewer } from '@/components/platform-admin/AuditLogViewer';
import { AdminManagement } from '@/components/platform-admin/AdminManagement';
import { PlatformSettings } from '@/components/platform-admin/PlatformSettings';
import { ScoringTools } from '@/components/platform-admin/ScoringTools';
import { FileText } from 'lucide-react';

export function PlatformAdminDashboard() {
  const navigate = useNavigate();
  const { data: adminInfo } = usePlatformAdmin();
  const [activeTab, setActiveTab] = useState('organizations');
  
  const hasPermission = (permission: string) => {
    return adminInfo?.permissions?.[permission] || false;
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold">Platform Administration</h1>
        <p className="text-muted-foreground">
          Manage all platform organizations, users, and settings
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${hasPermission('can_grant_admin_roles') && hasPermission('can_manage_platform') ? 7 : hasPermission('can_grant_admin_roles') || hasPermission('can_manage_platform') ? 6 : 5}, minmax(0, 1fr))` }}>
          <TabsTrigger value="organizations">Organizations</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
          
          {hasPermission('can_grant_admin_roles') && (
            <TabsTrigger value="admin-management">Admin Management</TabsTrigger>
          )}
          
          {hasPermission('can_manage_platform') && (
            <TabsTrigger value="settings">Settings</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="organizations" className="space-y-4">
          <OrganizationManagement />
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UserManagement />
        </TabsContent>

        <TabsContent value="metrics" className="space-y-4">
          <PlatformMetrics />
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <AuditLogViewer />
        </TabsContent>

        <TabsContent value="tools" className="space-y-4">
          <ScoringTools />
          <Card>
            <CardHeader>
              <CardTitle>Platform Tools</CardTitle>
              <CardDescription>
                Administrative tools for platform management and data export
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded border border-border bg-muted/50">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-5 w-5 text-primary" />
                      <p className="font-medium">Assessment Question Export</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Extract mySuccession Snapshot questions with metadata for MCF mapping and analysis
                    </p>
                  </div>
                  <Button onClick={() => navigate('/admin/assessment-export')} variant="outline">
                    Open Tool
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {hasPermission('can_grant_admin_roles') && (
          <TabsContent value="admin-management" className="space-y-4">
            <AdminManagement />
          </TabsContent>
        )}

        {hasPermission('can_manage_platform') && (
          <TabsContent value="settings" className="space-y-4">
            <PlatformSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
